import {
    CloudWatchLogsClient,
    CreateLogGroupCommand,
    CreateLogStreamCommand,
    PutLogEventsCommand,
    DescribeLogGroupsCommand,
    DescribeLogStreamsCommand,
    InputLogEvent,
} from '@aws-sdk/client-cloudwatch-logs';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { jwtDecode } from 'jwt-decode';
import { getCloudWatchConfig, validateCloudWatchConfig } from './config';

export enum LogLevel {
    INFO = 0,
    WARN = 1,
    ERROR = 2,
}

export interface LogEntry {
    level: LogLevel;
    message: string;
    timestamp: number;
    metadata?: Record<string, any>;
    userId?: string;
    userEmail?: string | null;
    sessionId?: string;
    component?: string;
    action?: string;
}

export interface CloudWatchLoggerConfig {
    region?: string;
    identityPoolId?: string;
    userPoolId?: string;
    logGroupName: string;
    minLogLevel: LogLevel;
    batchSize: number;
    batchTimeout: number;
    enableCloudWatchLogs: boolean;
}

class CloudWatchLogger {
    private static singletonInstance: CloudWatchLogger | null = null;
    private client: CloudWatchLogsClient | null = null;
    private config: CloudWatchLoggerConfig;
    private logBuffer: LogEntry[] = [];
    private batchTimer: NodeJS.Timeout | null = null;
    private logStreamName = '';
    private sequenceToken: string | undefined;
    private isInitialized = false;
    private sessionId: string;
    private userEmail: string | null = null;
    private initializationPromise: Promise<void> | null = null;

    // Static method to get singleton instance
    public static getInstance(): CloudWatchLogger | null {
        const config = getCloudWatchConfig();
        const errors = validateCloudWatchConfig(config);
        if (!CloudWatchLogger.singletonInstance && errors.length === 0) {
            CloudWatchLogger.singletonInstance = new CloudWatchLogger(config);
        }
        return CloudWatchLogger.singletonInstance;
    }

    // Static method to reset singleton (for testing/development)
    public static resetInstance(): void {
        if (CloudWatchLogger.singletonInstance) {
            CloudWatchLogger.singletonInstance.destroy();
        }
        CloudWatchLogger.singletonInstance = null;
    }

    private constructor(config: CloudWatchLoggerConfig) {
        this.config = config;
        this.sessionId = this.generateSessionId();
        this.logStreamName = `${this.sessionId}-${Date.now()}`;
    }

    private generateSessionId(): string {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }

    private extractUserEmailFromToken(idToken: string): string | null {
        try {
            const decoded: any = jwtDecode(idToken);
            return decoded.email || decoded['cognito:username'] || null;
        } catch (error) {
            console.error('Failed to decode JWT token:', error);
            return null;
        }
    }

    public async initializeClient(idToken?: string): Promise<void> {
        // If already initialized or initialization is in progress, return the existing promise
        if (this.isInitialized) {
            return Promise.resolve();
        }

        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        // Create the initialization promise
        this.initializationPromise = this.performInitialization(idToken);
        return this.initializationPromise;
    }

    private async performInitialization(idToken?: string): Promise<void> {
        try {
            if (!this.config.enableCloudWatchLogs) {
                console.log('CloudWatch logging is disabled');
                return;
            }

            if (!this.config.identityPoolId) {
                throw new Error('Cognito Identity Pool ID is required for secure frontend logging');
            }

            if (!idToken) {
                throw new Error('ID Token is required for CloudWatch authentication');
            }

            // Extract user email from token
            this.userEmail = this.extractUserEmailFromToken(idToken);

            // Use Cognito Identity Pool for secure, temporary credentials.
            // Pass clientConfig so the credential provider creates the client internally (avoids
            // type mismatch between @aws-sdk/client-cognito-identity and nested-clients).
            const loginKey = `cognito-idp.${this.config.region}.amazonaws.com/${this.config.userPoolId}`;
            const logins = {
                [loginKey]: idToken,
            };

            const credentials = fromCognitoIdentityPool({
                clientConfig: { region: this.config.region },
                identityPoolId: this.config.identityPoolId,
                logins,
            });

            this.client = new CloudWatchLogsClient({
                region: this.config.region,
                credentials,
            });

            await this.ensureLogGroupExists();
            await this.ensureLogStreamExists();
            this.isInitialized = true;
        } catch (error) {
            console.error('Failed to initialize CloudWatch Logger:', error);

            // Continue without CloudWatch logging
            this.config.enableCloudWatchLogs = false;
            this.isInitialized = false;
        }
    }

    private async ensureLogGroupExists(): Promise<void> {
        if (!this.client) return;

        try {
            const describeCommand = new DescribeLogGroupsCommand({
                logGroupNamePrefix: this.config.logGroupName,
            });

            const response = await this.client.send(describeCommand);
            const exists = response.logGroups?.some((group) => group.logGroupName === this.config.logGroupName);
            if (!exists) {
                const createCommand = new CreateLogGroupCommand({
                    logGroupName: this.config.logGroupName,
                });
                await this.client.send(createCommand);
            }
        } catch (error) {
            console.error('Error ensuring log group exists:', error);
        }
    }

    private async ensureLogStreamExists(): Promise<void> {
        if (!this.client) return;

        try {
            const describeCommand = new DescribeLogStreamsCommand({
                logGroupName: this.config.logGroupName,
                logStreamNamePrefix: this.logStreamName,
            });

            const response = await this.client.send(describeCommand);
            const exists = response.logStreams?.some((stream) => stream.logStreamName === this.logStreamName);

            if (!exists) {
                const createCommand = new CreateLogStreamCommand({
                    logGroupName: this.config.logGroupName,
                    logStreamName: this.logStreamName,
                });
                await this.client.send(createCommand);
            } else {
                // Get the sequence token if stream exists
                const stream = response.logStreams?.find((s) => s.logStreamName === this.logStreamName);
                this.sequenceToken = stream?.uploadSequenceToken;
            }
        } catch (error) {
            console.error('Error ensuring log stream exists:', error);
        }
    }

    private shouldLog(level: LogLevel): boolean {
        return level >= this.config.minLogLevel;
    }

    private addToBuffer(entry: LogEntry): void {
        if (!this.shouldLog(entry.level)) return;

        if (!this.config.enableCloudWatchLogs) return;

        this.logBuffer.push(entry);

        if (this.logBuffer.length >= this.config.batchSize) {
            this.flushLogs();
        } else if (!this.batchTimer) {
            this.batchTimer = setTimeout(() => {
                this.flushLogs();
            }, this.config.batchTimeout);
        }
    }

    private async flushLogs(): Promise<void> {
        if (this.logBuffer.length === 0) {
            return;
        }

        // If CloudWatch is disabled, just clear the buffer
        if (!this.config.enableCloudWatchLogs) {
            this.logBuffer = [];
            return;
        }

        // Wait for initialization to complete if it's still in progress
        if (this.initializationPromise && !this.isInitialized) {
            try {
                await this.initializationPromise;
            } catch (error) {
                // If initialization failed, clear buffer and return
                this.logBuffer = [];
                return;
            }
        }

        if (!this.client || !this.isInitialized) {
            return;
        }

        if (this.batchTimer) {
            clearTimeout(this.batchTimer);
            this.batchTimer = null;
        }

        const logsToSend = [...this.logBuffer];
        this.logBuffer = [];

        try {
            const logEvents: InputLogEvent[] = logsToSend.map((entry) => ({
                timestamp: entry.timestamp,
                message: JSON.stringify({
                    level: LogLevel[entry.level],
                    message: entry.message,
                    metadata: entry.metadata,
                    userId: entry.userId,
                    userEmail: entry.userEmail || this.userEmail,
                    sessionId: entry.sessionId || this.sessionId,
                    component: entry.component,
                    action: entry.action,
                    userAgent: navigator.userAgent,
                    url: window.location.href,
                }),
            }));

            // Only send logs if there are events to send
            if (logEvents.length === 0) {
                return;
            }

            const command = new PutLogEventsCommand({
                logGroupName: this.config.logGroupName,
                logStreamName: this.logStreamName,
                logEvents: logEvents.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)),
                sequenceToken: this.sequenceToken,
            });

            const response = await this.client.send(command);
            this.sequenceToken = response.nextSequenceToken;
        } catch (error) {
            console.error('Failed to send logs to CloudWatch:', error);
            // Re-add logs to buffer for retry (optional)
            this.logBuffer.unshift(...logsToSend);
        }
    }

    // Public logging methods
    public info(message: string, metadata?: Record<string, any>, component?: string, action?: string): void {
        this.addToBuffer({
            level: LogLevel.INFO,
            message,
            timestamp: Date.now(),
            metadata,
            component,
            action,
            sessionId: this.sessionId,
            userEmail: this.userEmail,
        });
    }

    public warn(message: string, metadata?: Record<string, any>, component?: string, action?: string): void {
        this.addToBuffer({
            level: LogLevel.WARN,
            message,
            timestamp: Date.now(),
            metadata,
            component,
            action,
            sessionId: this.sessionId,
            userEmail: this.userEmail,
        });
    }

    public error(
        message: string,
        error?: Error,
        metadata?: Record<string, any>,
        component?: string,
        action?: string
    ): void {
        const errorMetadata = {
            ...metadata,
            error: error
                ? {
                      name: error.name,
                      message: error.message,
                      stack: error.stack,
                  }
                : undefined,
        };

        this.addToBuffer({
            level: LogLevel.ERROR,
            message,
            timestamp: Date.now(),
            metadata: errorMetadata,
            component,
            action,
            sessionId: this.sessionId,
            userEmail: this.userEmail,
        });
    }

    // Set user context
    public setUserId(userId: string): void {
        this.logBuffer.forEach((entry) => {
            entry.userId = userId;
        });
    }

    // Force flush logs
    public async flush(): Promise<void> {
        await this.flushLogs();
    }

    // Cleanup
    public destroy(): void {
        if (this.batchTimer) {
            clearTimeout(this.batchTimer);
        }
        this.flushLogs();
    }
}

export default CloudWatchLogger;
